/* Orchestratore dell'editing: modalità modifica, step-list, form di dettaglio,
   salvataggio, storico versioni ed entità. Si aggancia all'App tramite hook. */
import type { DiagramDoc, FlowStep, PEvent } from "@fullsteps/shared";
import { api, type LockInfo, type ShareInfo, type User } from "../api.js";
import type { App } from "../app.js";
import { el } from "../render/svg.js";
import { STEP_LABEL } from "./describe.js";
import { openEntitiesModal } from "./entities.js";
import { buildStepForm } from "./forms.js";
import { deleteStep, insertBlock, insertStep, moveStep, slugify, updateStep } from "./ops.js";
import { SaveManager, type SaveStatus } from "./save-manager.js";
import { renderStepList, type AddKind } from "./step-list.js";
import { openVersionsModal } from "./versions.js";
import { openShareModal } from "./share.js";
import { openExportModal } from "../export/index.js";

export interface EditorCtx {
  user: User;
  diagramId: number;
  baseVersionId: number | null;
  share?: ShareInfo;
  lock: LockInfo | null;
  onBack?: () => void;
}

export function attachEditor(app: App, root: HTMLElement, ctx: EditorCtx): void {
  new Editor(app, root, ctx).mount();
}

class Editor {
  private editMode = false;
  private selIndex: number | null = null;
  private save: SaveManager;
  private canEdit: boolean;
  private drawer!: HTMLElement;
  private stepListEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private editToggle!: HTMLButtonElement;
  private shareBtn?: HTMLButtonElement;
  private share?: ShareInfo;

  constructor(
    private app: App,
    private root: HTMLElement,
    private ctx: EditorCtx,
  ) {
    this.canEdit = ctx.user.role === "admin" || ctx.user.role === "editor";
    this.save = new SaveManager(ctx.diagramId, ctx.user, ctx.baseVersionId, ctx.lock);
    this.share = ctx.share;
  }

  /** L'etichetta dice a colpo d'occhio se il diagramma è esposto. */
  private paintShare(): void {
    if (!this.shareBtn) return;
    const pub = this.share?.visibility === "public";
    this.shareBtn.textContent = pub ? "⇗ Condiviso" : "⇗ Condividi";
    this.shareBtn.classList.toggle("on", pub);
  }

  private get doc(): DiagramDoc {
    return this.app.doc;
  }

  mount(): void {
    this.buildBar();
    this.buildDrawer();

    this.app.onRenderDetail = (box, msg) => this.renderDetail(box, msg);
    this.app.onSelect = (n) => {
      const m = this.app.model.MSGS[n - 1];
      if (m) {
        this.selIndex = m.i;
        this.refreshStepList();
      }
    };

    this.save.onStatus = (s, d) => this.setStatus(s, d);
    this.save.onConflict = () => void this.reloadFromServer("Il diagramma è cambiato: ricaricato dall'ultima versione.");
    addEventListener("beforeunload", () => void this.save.release());

    const other = this.save.lockedByOther();
    if (other) this.setStatus("locked", other.name ?? undefined);
  }

  /* ---- barra strumenti (in coda a #bar) ---- */
  private buildBar(): void {
    const bar = this.root.querySelector("#bar") as HTMLElement;
    const box = el("div", "ed-bar");

    this.statusEl = el("span", "ed-status");

    // se c'è la barra dell'app, indietro e utente vivono lì: qui resterebbero
    // duplicati. Il tasto lo crea comunque l'editor, perché prima di uscire
    // deve rilasciare il lock.
    const backSlot = document.querySelector(".app-bar-back");
    if (this.ctx.onBack) {
      const back = el("button", "btn sm", "← Diagrammi") as HTMLButtonElement;
      back.type = "button";
      back.addEventListener("click", () => {
        void this.save.release();
        this.ctx.onBack!();
      });
      (backSlot ?? box).appendChild(back);
    }

    if (!backSlot) box.appendChild(el("span", "ed-user", `${this.ctx.user.name} · ${this.ctx.user.role}`));

    if (this.canEdit) {
      this.editToggle = el("button", "btn", "✎ Modifica") as HTMLButtonElement;
      this.editToggle.type = "button";
      this.editToggle.setAttribute("aria-pressed", "false");
      this.editToggle.addEventListener("click", () => this.toggleEdit());
      box.appendChild(this.editToggle);
    }

    const vers = el("button", "btn", "🕭 Versioni") as HTMLButtonElement;
    vers.type = "button";
    vers.addEventListener("click", () =>
      openVersionsModal(this.root, this.ctx.user, this.ctx.diagramId, () =>
        this.reloadFromServer("Versione ripristinata."),
      ),
    );
    box.appendChild(vers);

    const exp = el("button", "btn", "⤓ Esporta") as HTMLButtonElement;
    exp.type = "button";
    exp.addEventListener("click", () => openExportModal(this.root, this.doc));
    box.appendChild(exp);

    if (this.canEdit && this.ctx.share) {
      this.shareBtn = el("button", "btn", "") as HTMLButtonElement;
      this.shareBtn.type = "button";
      this.paintShare();
      this.shareBtn.addEventListener("click", () =>
        openShareModal(this.root, {
          diagramId: this.ctx.diagramId,
          doc: this.doc,
          share: this.share!,
          onChange: (s) => {
            this.share = s;
            this.paintShare();
          },
        }),
      );
      box.appendChild(this.shareBtn);
    }

    if (this.canEdit) {
      const ent = el("button", "btn", "⚑ Attori/Doc") as HTMLButtonElement;
      ent.type = "button";
      ent.addEventListener("click", () =>
        openEntitiesModal(this.root, this.doc, (d) => this.applyDoc(d)),
      );
      box.appendChild(ent);
    }

    box.appendChild(this.statusEl);
    bar.appendChild(box);
  }

  private buildDrawer(): void {
    const main = this.root.querySelector("#main") as HTMLElement;
    this.drawer = el("aside", "ed-drawer");
    this.drawer.hidden = true;
    const head = el("div", "ed-drawer-head");
    head.appendChild(el("h2", undefined, "Passi"));
    this.drawer.appendChild(head);
    this.stepListEl = el("div", "ed-drawer-body");
    this.drawer.appendChild(this.stepListEl);
    main.insertBefore(this.drawer, main.firstChild);
  }

  private toggleEdit(): void {
    this.editMode = !this.editMode;
    this.editToggle.setAttribute("aria-pressed", String(this.editMode));
    this.editToggle.textContent = this.editMode ? "✓ Fine modifica" : "✎ Modifica";
    this.drawer.hidden = !this.editMode;
    if (this.editMode) this.refreshStepList();
    this.app.renderDetail();
  }

  /* ---- step-list ---- */
  private refreshStepList(): void {
    if (!this.editMode) return;
    renderStepList(this.stepListEl, this.doc, this.selIndex, {
      onSelect: (i) => this.selectIndex(i),
      onDelete: (i) => this.applyStructural(deleteStep(this.doc, i), Math.max(0, i - 1)),
      onMove: (i, dir) => this.applyStructural(moveStep(this.doc, i, dir), i + dir),
      onAddAfter: (i, kind) => this.addAfter(i, kind),
    });
  }

  private msgNumberAt(i: number): number | null {
    return this.app.model.MSGS.find((m) => m.i === i)?.n ?? null;
  }

  private selectIndex(i: number): void {
    this.selIndex = i;
    const step = this.doc.flow[i];
    this.app.S.sel = step?.t === "msg" ? this.msgNumberAt(i) : null;
    this.app.draw();
    this.app.renderDetail();
    this.refreshStepList();
  }

  private addAfter(i: number, kind: AddKind): void {
    let d: DiagramDoc;
    if (kind === "alt" || kind === "opt" || kind === "loop") d = insertBlock(this.doc, i + 1, kind);
    else d = insertStep(this.doc, i + 1, this.newStep(kind));
    this.applyStructural(d, i + 1);
  }

  private newStep(kind: Exclude<AddKind, "alt" | "opt" | "loop">): FlowStep {
    const p0 = this.doc.participants[0]?.id ?? "";
    const p1 = this.doc.participants[1]?.id ?? p0;
    switch (kind) {
      case "msg":
        return { t: "msg", from: p0, to: p1, text: "nuovo messaggio" };
      case "note":
        return { t: "note", over: p0, text: "nota" };
      case "act":
        return { t: "act", a: p0 };
      case "deact":
        return { t: "deact", a: p0 };
      case "create":
        return { t: "create", a: p0 };
      case "destroy":
        return { t: "destroy", a: p0 };
      case "phase": {
        const ids = new Set(this.doc.flow.filter((s) => s.t === "phase").map((s) => (s as { id: string }).id));
        return { t: "phase", id: slugify("fase", ids), title: "Nuova fase" };
      }
    }
  }

  /* ---- applicazione modifiche ---- */
  private applyStep(i: number, step: FlowStep): void {
    const d = updateStep(this.doc, i, step);
    this.app.setDoc(d, { keepDetail: true }); // non ricostruire il form (focus)
    this.save.schedule(d);
    this.refreshStepList();
  }

  private applyStructural(d: DiagramDoc, newSel: number): void {
    if (d === this.doc) return; // op non consentita
    this.selIndex = Math.min(newSel, d.flow.length - 1);
    if (this.selIndex < 0) this.selIndex = null;
    const step = this.selIndex != null ? d.flow[this.selIndex] : undefined;
    this.app.S.sel = step?.t === "msg" ? null : null; // ricalcolato dopo setDoc
    this.app.setDoc(d);
    if (this.selIndex != null && d.flow[this.selIndex]?.t === "msg")
      this.app.S.sel = this.app.model.MSGS.find((m) => m.i === this.selIndex)?.n ?? null;
    this.app.draw();
    this.app.renderDetail();
    this.save.schedule(d);
    this.refreshStepList();
  }

  private applyDoc(d: DiagramDoc): void {
    this.app.setDoc(d);
    this.save.schedule(d);
    this.refreshStepList();
  }

  /* ---- pannello dettaglio in modalità modifica ---- */
  private renderDetail(box: HTMLElement, msg: PEvent | null): boolean {
    if (!this.editMode) return false;
    if (this.selIndex == null && msg) this.selIndex = msg.i;
    box.textContent = "";
    box.appendChild(el("h2", undefined, "Modifica passo"));
    if (this.selIndex == null) {
      box.appendChild(el("p", "hint", "Seleziona un passo dal diagramma o dalla lista a sinistra."));
      return true;
    }
    const i = this.selIndex;
    const step = this.doc.flow[i];
    if (!step) {
      this.selIndex = null;
      box.appendChild(el("p", "hint", "Passo non più presente."));
      return true;
    }
    const head = el("p", "ed-stephead", `${STEP_LABEL[step.t]} · passo ${i + 1} di ${this.doc.flow.length}`);
    box.appendChild(head);
    box.appendChild(buildStepForm(step, this.doc, (s) => this.applyStep(i, s)));

    if (step.t !== "else" && step.t !== "end") {
      const del = el("button", "btn sm danger", "Elimina passo") as HTMLButtonElement;
      del.type = "button";
      del.addEventListener("click", () => this.applyStructural(deleteStep(this.doc, i), Math.max(0, i - 1)));
      box.appendChild(del);
    }
    return true;
  }

  /* ---- stato + conflitti ---- */
  private setStatus(s: SaveStatus, detail?: string): void {
    const map: Record<SaveStatus, string> = {
      idle: "",
      dirty: "· modifiche non salvate",
      saving: "· salvataggio…",
      saved: "· salvato",
      error: "· errore" + (detail ? `: ${detail}` : ""),
      conflict: "· conflitto",
      locked: "· in modifica da " + (detail ?? "un altro utente") + " (sola lettura)",
    };
    this.statusEl.textContent = map[s];
    this.statusEl.className = "ed-status " + s;
  }

  private async reloadFromServer(note: string): Promise<void> {
    const state = await api.getDiagram(this.ctx.diagramId);
    if (!state.doc) return;
    this.app.setDoc(state.doc);
    if (state.version) this.save.setBaseVersion(state.version.id);
    this.selIndex = null;
    this.refreshStepList();
    this.setStatus("saved", note);
    this.statusEl.textContent = "· " + note;
  }
}
